import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  Download, Upload, Clock, Wifi, WifiOff, RefreshCw, HardDrive,
  CheckCircle2, AlertCircle, Loader2, Calendar, Shield
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const Backup = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineCount, setOfflineCount] = useState(api.getOfflineQueueCount());
  const [syncing, setSyncing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [lastBackup, setLastBackup] = useState(localStorage.getItem('colabella_last_backup') || null);
  const [autoBackup, setAutoBackup] = useState(localStorage.getItem('colabella_auto_backup') === 'true');
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);
  const pageRef = useRef(null);

  useEffect(() => {
    const onOnline = () => { setIsOnline(true); setOfflineCount(api.getOfflineQueueCount()); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const interval = setInterval(() => setOfflineCount(api.getOfflineQueueCount()), 5000);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); clearInterval(interval); };
  }, []);

  // Auto backup scheduler
  useEffect(() => {
    if (!autoBackup) return;
    const checkAutoBackup = () => {
      const now = new Date();
      const lastRun = localStorage.getItem('colabella_auto_backup_last');
      const today = now.toISOString().split('T')[0];
      if (lastRun !== today && now.getHours() === 0) {
        handleDownloadBackup();
        localStorage.setItem('colabella_auto_backup_last', today);
      }
    };
    const interval = setInterval(checkAutoBackup, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [autoBackup]);

  const handleDownloadBackup = async () => {
    setDownloading(true);
    setMessage(null);
    try {
      const data = await api.getBackup();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      const time = new Date().toTimeString().slice(0, 5).replace(':', '-');
      a.download = `Colabella_Backup_${date}_${time}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const now = new Date().toLocaleString('it-IT');
      setLastBackup(now);
      localStorage.setItem('colabella_last_backup', now);
      setMessage({ type: 'success', text: 'Backup scaricato con successo!' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Errore nel backup: ' + (e.message || 'Controlla la connessione') });
    }
    setDownloading(false);
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm('ATTENZIONE: Il ripristino sovrascriverà TUTTI i dati attuali. Continuare?')) return;
    setRestoring(true);
    setMessage(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.collections) throw new Error('File di backup non valido');
      const result = await api.restoreBackup(data);
      setMessage({ type: 'success', text: `Ripristino completato! ${Object.keys(result.collections).length} collezioni ripristinate.` });
    } catch (e) {
      setMessage({ type: 'error', text: 'Errore nel ripristino: ' + e.message });
    }
    setRestoring(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSync = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const result = await api.syncOffline();
      setOfflineCount(api.getOfflineQueueCount());
      setMessage({ type: 'success', text: `Sincronizzate ${result.synced} operazioni su ${result.total}` });
    } catch (e) {
      setMessage({ type: 'error', text: 'Errore nella sincronizzazione' });
    }
    setSyncing(false);
  };

  const toggleAutoBackup = () => {
    const newVal = !autoBackup;
    setAutoBackup(newVal);
    localStorage.setItem('colabella_auto_backup', String(newVal));
  };

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Backup e Sincronizzazione" subtitle="Gestione dati offline e backup" />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Backup e Sincronizzazione</h1>
          <p className="page-subtitle">Lavora offline, backup automatico e ripristino dati</p>
        </div>
        <div className="header-right">
          <SectionActions sectionTitle="Backup" contentRef={pageRef} />
        </div>
      </div>

      {/* Status Bar */}
      <div className="backup-status-bar">
        <div className={`backup-status ${isOnline ? 'backup-online' : 'backup-offline'}`}>
          {isOnline ? <Wifi size={20} /> : <WifiOff size={20} />}
          <span>{isOnline ? 'Online — Connesso' : 'Offline — Le operazioni vengono salvate localmente'}</span>
        </div>
        {offlineCount > 0 && (
          <div className="backup-queue-badge">
            <AlertCircle size={16} />
            <span>{offlineCount} operazioni in coda</span>
            <button className="backup-sync-btn" onClick={handleSync} disabled={syncing || !isOnline}>
              <RefreshCw size={14} className={syncing ? 'spin' : ''} />
              {syncing ? 'Sincronizzazione...' : 'Sincronizza Ora'}
            </button>
          </div>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`backup-message ${message.type === 'success' ? 'backup-msg-ok' : 'backup-msg-err'}`}>
          {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Cards */}
      <div className="backup-cards">
        {/* Download Backup */}
        <div className="backup-card">
          <div className="backup-card-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}>
            <Download size={28} />
          </div>
          <h3>Scarica Backup</h3>
          <p>Scarica tutti i dati dell'app in un file JSON sul tuo computer. Include cantieri, operai, timbrature, magazzino, paghe e tutto il resto.</p>
          {lastBackup && <span className="backup-last">Ultimo backup: {lastBackup}</span>}
          <button className="backup-action-btn backup-btn-download" onClick={handleDownloadBackup} disabled={downloading}>
            {downloading ? <><Loader2 size={16} className="spin" /> Scaricamento...</> : <><Download size={16} /> Scarica Backup Ora</>}
          </button>
        </div>

        {/* Auto Backup */}
        <div className="backup-card">
          <div className="backup-card-icon" style={{ background: '#f0fdf4', color: '#22c55e' }}>
            <Clock size={28} />
          </div>
          <h3>Backup Automatico</h3>
          <p>Attiva il backup automatico giornaliero. Ogni giorno a mezzanotte, il backup verrà scaricato automaticamente se l'app è aperta.</p>
          <div className="backup-toggle-row">
            <span>Backup automatico alle 00:00</span>
            <button className={`backup-toggle ${autoBackup ? 'backup-toggle-on' : ''}`} onClick={toggleAutoBackup}>
              {autoBackup ? 'ATTIVO' : 'DISATTIVO'}
            </button>
          </div>
          <button className="backup-action-btn backup-btn-manual" onClick={handleDownloadBackup} disabled={downloading}>
            <HardDrive size={16} /> Backup Manuale
          </button>
        </div>

        {/* Restore */}
        <div className="backup-card">
          <div className="backup-card-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <Upload size={28} />
          </div>
          <h3>Ripristina Dati</h3>
          <p>Carica un file di backup precedente per ripristinare tutti i dati. <strong>Attenzione: sovrascriverà i dati attuali!</strong></p>
          <input type="file" ref={fileInputRef} accept=".json" onChange={handleRestore} style={{ display: 'none' }} />
          <button className="backup-action-btn backup-btn-restore" onClick={() => fileInputRef.current?.click()} disabled={restoring}>
            {restoring ? <><Loader2 size={16} className="spin" /> Ripristino...</> : <><Upload size={16} /> Carica File di Backup</>}
          </button>
        </div>

        {/* Offline Mode */}
        <div className="backup-card">
          <div className="backup-card-icon" style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
            <Shield size={28} />
          </div>
          <h3>Modalità Offline</h3>
          <p>Quando non hai internet, l'app continua a funzionare. Le operazioni (timbrature, modifiche, ecc.) vengono salvate localmente e sincronizzate automaticamente quando torni online.</p>
          <div className="backup-info-row">
            <span className="backup-info-item">
              {isOnline ? <Wifi size={14} color="#22c55e" /> : <WifiOff size={14} color="#ef4444" />}
              Stato: <strong>{isOnline ? 'Online' : 'Offline'}</strong>
            </span>
            <span className="backup-info-item">
              <HardDrive size={14} />
              Coda: <strong>{offlineCount} operazioni</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Backup;
